/**
 * Aggregates appointments for pregnancy/postpartum **journey** consultation hubs only.
 * Menopause uses `useMenopauseConsultationAppointments` — keep implementations separate.
 */
import {useMemo} from 'react'

import {
  useConsultationsMyAppointmentsQuery,
  useConsultationsProgramDetailQuery,
} from '@progyny/generated/graphql'
import type {ConsultationListAppointmentItem} from '@progyny/pages/Consultations/types/consultationListAppointmentItem'
import {
  filterPastAppointments,
  filterUpcomingAppointments,
  formatAppointmentDate,
  formatAppointmentTime,
  getAppointmentsBySlug,
  getConsultationsBySlug,
} from '@progyny/utils/appointmentHelpers'

type SessionStatus = {
  session?: {sessionNumber?: number | null; title?: string | null} | null
  appointment?: {id?: string | null} | null
} | null

export type P3HubAppointmentItem = ConsultationListAppointmentItem

function sessionStatusesFromProgramDetail(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- GraphQL query shape varies by program
  data: any,
  slug: string | undefined
): SessionStatus[] | undefined {
  if (!slug?.length || !data?.consultations?.program) return undefined
  const program = data.consultations.program
  const enrollment = data.consultations.myProgramEnrollments?.find(
    (e: {program?: {id?: string | null} | null} | null) =>
      e?.program?.id === program.id
  )
  return enrollment?.sessionStatuses ?? undefined
}

function normalizeP3ProgramAppointment(
  apt: {
    id?: string | null
    scheduledTime?: string | null
    status?: string | null
    programEnrollment?: {
      program?: {
        slug?: string | null
        description?: string | null
        title?: string | null
        sessions?: Array<{title?: string | null} | null> | null
      } | null
    } | null
    consultationEnrollment?: {
      consultation?: {title?: string | null} | null
    } | null
    calcomBookingUid?: string | null
    canReschedule?: boolean | null
    canJoin?: boolean | null
    meetingLink?: string | null
    bookingLanguage?: string | null
  },
  sessionStatusesByProgramSlug: Map<string, SessionStatus[] | undefined>
): P3HubAppointmentItem | null {
  if (!apt?.id) return null
  const program = apt.programEnrollment?.program
  const consultation = apt.consultationEnrollment?.consultation
  const isConsultationOnboarding = !!consultation
  const programSlug = program?.slug ?? undefined
  const sessionStatuses = programSlug
    ? sessionStatusesByProgramSlug.get(programSlug)
    : undefined

  const sessionStatus = sessionStatuses?.find(
    (ss) => ss?.appointment?.id === apt.id
  )
  const sessionNumber = sessionStatus?.session?.sessionNumber ?? undefined
  const sessionTitle = isConsultationOnboarding
    ? (consultation?.title ?? 'Your onboarding call')
    : (sessionStatus?.session?.title ??
      program?.sessions?.[0]?.title ??
      program?.title ??
      'Emotional support')

  return {
    id: apt.id,
    scheduledTime: apt.scheduledTime,
    status: apt.status ?? 'SCHEDULED',
    type: isConsultationOnboarding ? 'onboarding' : 'emotional_program',
    title: sessionTitle ?? 'Emotional support',
    description: program?.description ?? undefined,
    programSlug,
    sessionNumber: sessionNumber != null ? Number(sessionNumber) : undefined,
    appointment: {
      id: apt.id,
      calcomBookingUid: apt.calcomBookingUid,
      canReschedule: apt.canReschedule,
      canJoin: apt.canJoin,
      meetingLink: apt.meetingLink,
      bookingLanguage: apt.bookingLanguage ?? undefined,
    },
  }
}

export interface UseP3ConsultationAppointmentsParams {
  onboardingConsultationSlug: string
  programSlugs: readonly string[]
}

export function useP3ConsultationAppointments({
  onboardingConsultationSlug,
  programSlugs,
}: UseP3ConsultationAppointmentsParams) {
  const {
    data: appointmentsData,
    loading: appointmentsLoading,
    refetch: refetchAppointments,
  } = useConsultationsMyAppointmentsQuery({
    fetchPolicy: 'cache-first',
  })

  const s0 = programSlugs[0]
  const s1 = programSlugs[1]
  const s2 = programSlugs[2]
  const s3 = programSlugs[3]
  const s4 = programSlugs[4]
  const s5 = programSlugs[5]

  const r0 = useConsultationsProgramDetailQuery({
    variables: {slug: s0 ?? '__none__'},
    skip: !s0,
    fetchPolicy: 'cache-first',
  })
  const r1 = useConsultationsProgramDetailQuery({
    variables: {slug: s1 ?? '__none__'},
    skip: !s1,
    fetchPolicy: 'cache-first',
  })
  const r2 = useConsultationsProgramDetailQuery({
    variables: {slug: s2 ?? '__none__'},
    skip: !s2,
    fetchPolicy: 'cache-first',
  })
  const r3 = useConsultationsProgramDetailQuery({
    variables: {slug: s3 ?? '__none__'},
    skip: !s3,
    fetchPolicy: 'cache-first',
  })
  const r4 = useConsultationsProgramDetailQuery({
    variables: {slug: s4 ?? '__none__'},
    skip: !s4,
    fetchPolicy: 'cache-first',
  })
  const r5 = useConsultationsProgramDetailQuery({
    variables: {slug: s5 ?? '__none__'},
    skip: !s5,
    fetchPolicy: 'cache-first',
  })

  const programDetailLoading =
    (!!s0 && r0.loading) ||
    (!!s1 && r1.loading) ||
    (!!s2 && r2.loading) ||
    (!!s3 && r3.loading) ||
    (!!s4 && r4.loading) ||
    (!!s5 && r5.loading)

  const appointmentsLoadingCombined =
    appointmentsLoading || programDetailLoading

  const sessionStatusesByProgramSlug = useMemo(() => {
    const map = new Map<string, SessionStatus[] | undefined>()
    const pairs: Array<[string | undefined, {data: typeof r0.data}]> = [
      [s0, r0],
      [s1, r1],
      [s2, r2],
      [s3, r3],
      [s4, r4],
      [s5, r5],
    ]
    pairs.forEach(([slug, result]) => {
      if (!slug) return
      map.set(slug, sessionStatusesFromProgramDetail(result.data, slug))
    })
    return map
  }, [s0, s1, s2, s3, s4, s5, r0, r1, r2, r3, r4, r5])

  return useMemo(() => {
    const programAppointmentsArrays = programSlugs.map((slug) =>
      getAppointmentsBySlug(slug, appointmentsData)
    )
    const programConsultations = getConsultationsBySlug(
      onboardingConsultationSlug,
      appointmentsData
    )
    const combined = [
      ...programAppointmentsArrays.flat(),
      ...programConsultations,
    ]

    const programUpcoming = filterUpcomingAppointments(combined)
    const programPast = filterPastAppointments(combined)

    const upcoming: P3HubAppointmentItem[] = []
    const past: P3HubAppointmentItem[] = []

    programUpcoming.forEach((apt) => {
      const item = normalizeP3ProgramAppointment(
        apt,
        sessionStatusesByProgramSlug
      )
      if (item) upcoming.push(item)
    })
    programPast.forEach((apt) => {
      const item = normalizeP3ProgramAppointment(
        apt,
        sessionStatusesByProgramSlug
      )
      if (item) past.push(item)
    })

    upcoming.sort((a, b) => {
      const tA = a.scheduledTime ? new Date(a.scheduledTime).getTime() : 0
      const tB = b.scheduledTime ? new Date(b.scheduledTime).getTime() : 0
      return tA - tB
    })

    past.sort((a, b) => {
      const tA = a.scheduledTime ? new Date(a.scheduledTime).getTime() : 0
      const tB = b.scheduledTime ? new Date(b.scheduledTime).getTime() : 0
      return tB - tA
    })

    return {
      upcoming,
      past,
      hasUpcoming: upcoming.length > 0,
      hasPast: past.length > 0,
      loading: appointmentsLoadingCombined,
      formatDate: formatAppointmentDate,
      formatTime: formatAppointmentTime,
      refetchAppointments,
    }
  }, [
    appointmentsData,
    appointmentsLoadingCombined,
    onboardingConsultationSlug,
    programSlugs,
    sessionStatusesByProgramSlug,
    refetchAppointments,
  ])
}
